import { useEffect, useState } from 'react';
import { HiOutlineRocketLaunch } from 'react-icons/hi2';
import { toastCustom } from '../utils/toasts';
import UserDTO, { displayUserPermissions, UserBusiness } from '../models/user';
import MenuDropdown from './atoms/MenuDropdown';
import { useRemoveUserFromBusinessMutation } from '../store';
import FilterTable, { BaseData } from './Table/Table';
import CustomModal from './atoms/CustomModal';
import Button from './atoms/Button';
import EmptyTable from './Table/EmptyTable';
import EditUser from './EditUser';

interface Props {
  users: UserDTO[];
  businessId: string;
  customButton?: JSX.Element;
}

type ParsedUser = UserDTO & UserBusiness;

interface TableUser extends BaseData {
  id: string;
  username: JSX.Element;
  permissions: JSX.Element;
  active: JSX.Element;
  email: JSX.Element;
}

const parseUser = (businessId: string, user: UserDTO): ParsedUser => {
  const userBiz = user.user_businesses.find(
    (ub) => ub.business_id === businessId,
  );
  if (!userBiz) {
    throw new Error('no user business found');
  }
  return {
    ...user,
    ...userBiz,
  };
};

const UsersTable: React.FC<Props> = ({ users, businessId, customButton }) => {
  const [parsedUsers, setParsedUsers] = useState<ParsedUser[]>([]);
  const [removeUserFromBusiness, { isLoading: isRemovingUser }] = useRemoveUserFromBusinessMutation();
  const [showModal, setShowModal] = useState(false);
  const [userBusinessId, setUserBusinessId] = useState<string | null>(null);
  const [modalType, setModalType] = useState<'delete' | 'invite'>('delete');
  const [userToEdit, setUserToEdit] = useState<ParsedUser | null>(null);
  const [countdown, setCountdown] = useState(0);

  const handleConfirmRemoveUser = () => {
    if (userBusinessId) {
      toastCustom('Eliminando usuario...', {
        icon: '⏳',
        duration: Infinity,
      });
      removeUserFromBusiness({ userBusinessId, businessId }).unwrap()
        .then(() => {
          toastCustom('Usuario eliminado correctamente', {
            icon: '✅',
          });
        })
        .catch(() => {
          toastCustom('Error al eliminar usuario', {
            icon: '❌',
          });
        });
      setShowModal(false);
    }
  };

  useEffect(() => {
    setParsedUsers(users.map((u) => parseUser(businessId, u)));
  }, [users]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [countdown]);

  const config = [
    {
      label: 'Usuario',
      id: 'username',
    },
    {
      label: 'Correo',
      id: 'email',
    },
    {
      label: 'Permisos',
      id: 'permissions',
    },
    {
      label: 'Activo',
      id: 'active',
    },
  ];

  const onEditUser = (user: ParsedUser) => {
    setUserToEdit(user);
    setModalType('invite');
    setShowModal(true);
  };

  const onDeleteUser = (user: ParsedUser) => {
    setUserBusinessId(user.id);
    setModalType('delete');
    setShowModal(true);
    setCountdown(5);
  };

  const actions = (row: TableUser) => (
      <MenuDropdown<ParsedUser>
        options={[
          {
            label: 'Editar usuario',
            onClick: (user: ParsedUser) => onEditUser(user),
          },
          {
            label: 'Eliminar usuario',
            onClick: (user: ParsedUser) => onDeleteUser(user),
          },
        ]}
        data={parsedUsers.find((u) => u.id === row.id) || parsedUsers[0]}
        loading={isRemovingUser}
      />
  );

  const tableRows: TableUser[] = parsedUsers.map((user) => ({
    id: user.id,
    username: <p className="whitespace-nowrap">{user.username}</p>,
    permissions: <p className="whitespace-nowrap">{displayUserPermissions[user.permissions as keyof typeof displayUserPermissions]}</p>,
    active: user.active ? (
      <div className='tag-primary'>
        Activo
      </div>
    ) : (
      <div className='tag-warning'>
        Inactivo
      </div>
    ),
    email: <p className="whitespace-nowrap">{user.email}</p>,
  }));

  return (
    <div>
      {parsedUsers.length > 0 && (
        <div>
          <FilterTable
            rows={tableRows}
            headCells={config}
            actions={actions}
            pagination={{ pageSize: 10, enabled: true }}
            searchEnabled
            customButtons={customButton ? [customButton] : []}
            tableId="users-table"
          />
        </div>
      )}
      {parsedUsers.length === 0 && (
        <EmptyTable
          title="Aún no hay usuarios"
          info="Por el momento no hay usuarios registrados"
          icon={HiOutlineRocketLaunch}
          iconColor="text-secondary-500"
        />
      )}
      <CustomModal
        onRequestClose={() => setShowModal(false)}
        isOpen={showModal}
        hideCancelButton
        overflowVisible
      >
        <div className='max-w-2xl'>
          {modalType === 'delete' && (
            <div>
              <div className="flex flex-col mb-4">
                <h1 className="text-xl font-medium mb-2">Eliminar usuario</h1>
                <p className="text-sm">
                  ¿Estás seguro de querer eliminar este usuario del evento?
                </p>
                <p className='text-sm text-red-600 font-medium'>Esto puede llegar a afectar las métricas de los eventos donde el usuario vendió entradas, es recomendable desactivarlo en vez de eliminarlo.</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button variant="primary" onClick={handleConfirmRemoveUser} loading={isRemovingUser} disabled={countdown > 0}>
                  {countdown > 0 ? `Eliminar (${countdown})` : 'Eliminar'}
                </Button>
              </div>
            </div>
          )}
          {modalType === 'invite' && (
            <div className='md:min-w-[400px]'>
              <h1 className="text-xl font-medium mb-2">Editar usuario</h1>
              <p className="text-text-tertiary text-sm mb-4">
                Editá permisos y estado de tu usuario
              </p>
              <EditUser user={userToEdit} onSuccess={() => { setShowModal(false); setUserToEdit(null); }} />
            </div>
          )}
        </div>
      </CustomModal >
    </div >
  );
};

export default UsersTable;
